import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLMedia = () => {
    const multipleFormatCode = `<video controls width="640" height="360">
    <!-- Browser picks first format it supports --&gt;
    <source src="video.webm" type="video/webm">
    <source src="video.mp4" type="video/mp4">
    
    <!-- Fallback for very old browsers --&gt;
    <p>Your browser doesn't support HTML5 video. 
       <a href="video.mp4">Download the video</a> instead.</p>
</video>`;

    const ffmpegCompressionCode = `# Compress video for web (balanced quality/size)
ffmpeg -i input.mp4 -c:v libx264 -crf 23 -preset medium -c:a aac -b:a 128k output.mp4

# Compress to WebM for even smaller size
ffmpeg -i input.mp4 -c:v libvpx-vp9 -crf 30 -b:v 0 -c:a libopus output.webm

# Options explained:
# -crf 23: Constant quality (0-51, lower = better, 23 is good balance)
# -preset medium: Encoding speed vs file size tradeoff
# -b:a 128k: Audio bitrate (128kbps is standard)`;

    const adaptiveStreamingCode = `<!-- Using HLS.js library --&gt;
<video id="video" controls></video>
<script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
<script>
  const video = document.getElementById('video');
  const hls = new Hls();
  hls.loadSource('video-playlist.m3u8'); // Adaptive playlist
  hls.attachMedia(video);
</script>`;

    const lazyLoadingCode = `<!-- Videos below fold don't load until scrolled into view --&gt;
<video controls preload="none" poster="thumbnail.jpg">
    <source src="video.mp4" type="video/mp4">
</video>

<!-- Or use loading attribute (experimental) --&gt;
<video controls loading="lazy">
    <source src="video.mp4" type="video/mp4">
</video>`;

    const posterImageCode = `<video controls poster="thumbnail.jpg" preload="metadata">
    <source src="video.mp4" type="video/mp4">
</video>`;

    const captionsCode = `<video controls>
    <source src="video.mp4" type="video/mp4">
    
    <!-- Captions in multiple languages --&gt;
    <track kind="captions" src="captions-en.vtt" srclang="en" label="English" default>
    <track kind="captions" src="captions-es.vtt" srclang="es" label="Espaol">
    <track kind="captions" src="captions-fr.vtt" srclang="fr" label="Franais">
    
    <!-- Subtitles (translations) --&gt;
    <track kind="subtitles" src="subtitles-de.vtt" srclang="de" label="Deutsch">
</video>`;

    const webVttCode = `WEBVTT

00:00:00.000 --&gt; 00:00:03.000
Welcome to our tutorial on HTML5 video.

00:00:03.500 --&gt; 00:00:07.000
Today we'll learn about video optimization.

00:00:07.500 --&gt; 00:00:11.000
[Background music playing]`;

    const audioDescriptionCode = `<video controls>
    <source src="video.mp4" type="video/mp4">
    <track kind="descriptions" src="audio-descriptions.vtt" srclang="en" label="Audio Descriptions">
</video>`;

    const transcriptCode = `<video controls>
    <source src="video.mp4" type="video/mp4">
</video>

<details>
    <summary>View Transcript</summary>
    <p><strong>00:00</strong> - Welcome to our tutorial...</p>
    <p><strong>00:15</strong> - In this video, we'll cover...</p>
</details>`;

    const optimizedAudioCode = `<audio controls preload="metadata">
    <source src="audio.mp3" type="audio/mpeg">
    <source src="audio.ogg" type="audio/ogg">
    <p>Your browser doesn't support HTML5 audio. 
       <a href="audio.mp3">Download the audio file</a>.</p>
</audio>`;

    const ffmpegAudioCode = `# Convert to MP3 at 128kbps (good for speech)
ffmpeg -i input.wav -codec:a libmp3lame -b:a 128k output.mp3

# Convert to AAC (better quality)
ffmpeg -i input.wav -c:a aac -b:a 192k output.m4a

# Convert to mono for podcasts (smaller size)
ffmpeg -i input.wav -ac 1 -b:a 96k output.mp3`;

    const preloadCode = `<video controls preload="metadata">
    <source src="video.mp4" type="video/mp4">
</video>`;

    const posterAttributeCode = `<video controls poster="thumbnail.jpg">
    <source src="video.mp4" type="video/mp4">
</video>`;

    const mutedAttributeCode = `<!-- Autoplay only works if muted --&gt;
<video autoplay muted loop>
    <source src="background-video.mp4" type="video/mp4">
</video>`;

    const playsInlineCode = `<!-- Stays inline on mobile --&gt;
<video controls playsinline>
    <source src="video.mp4" type="video/mp4">
</video>`;

    const youtubeEmbedCode = `<!-- YouTube embed (recommended for public videos) --&gt;
<iframe width="560" height="315" 
        src="https://www.youtube.com/embed/VIDEO_ID"
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen>
</iframe>`;

    const productionVideoCode = `<figure>
    <video 
        controls 
        preload="metadata"
        poster="thumbnail-optimized.jpg"
        width="640" 
        height="360"
        playsinline
    >
        <!-- Multiple formats for compatibility --&gt;
        <source src="video-720p.webm" type="video/webm">
        <source src="video-720p.mp4" type="video/mp4">
        
        <!-- Captions for accessibility --&gt;
        <track kind="captions" src="captions-en.vtt" srclang="en" label="English" default>
        <track kind="captions" src="captions-es.vtt" srclang="es" label="Espaol">
        
        <!-- Fallback for very old browsers --&gt;
        <p>Your browser doesn't support HTML5 video. 
           <a href="video-720p.mp4">Download the video</a> instead.</p>
    </video>
    
    <figcaption>
        Tutorial: Understanding HTML5 Media Optimization
    </figcaption>
</figure>

<!-- Transcript for SEO and accessibility --&gt;
<details>
    <summary>View Full Transcript</summary>
    <div class="transcript">
        <p><strong>[00:00]</strong> Welcome to this tutorial...</p>
        <p><strong>[00:15]</strong> In this video, we'll explore...</p>
    </div>
</details>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Audio & Video</div>
                <h1>Media Optimization & Best Practices</h1>
                <p className="hero-lead">
                    Master multimedia in HTML5. Learn how to optimize audio and video for performance, accessibility,
                    and user experience across all devices and browsers.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Media Optimization Matters</h2>
                <p>
                    Unoptimized media files can severely impact your website's performance, user experience, and accessibility.
                </p>

                <div className="impact-stats">
                    <div className="stat-card">
                        <div className="stat-number">53%</div>
                        <p>of mobile users abandon sites that take over 3 seconds to load</p>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">80%</div>
                        <p>of page weight typically comes from images and videos</p>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">15%</div>
                        <p>of users have hearing or vision impairments requiring captions/transcripts</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Video Optimization Fundamentals</h2>
                <p>
                    Optimizing video involves balancing file size, quality, and compatibility.
                </p>

                <h3>1. Choose the Right Format</h3>
                <div className="format-comparison">
                    <table>
                        <thead>
                            <tr>
                                <th>Format</th>
                                <th>File Extension</th>
                                <th>Browser Support</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>MP4 (H.264)</strong></td>
                                <td>.mp4</td>
                                <td>All modern browsers</td>
                                <td>Primary format (best compatibility)</td>
                            </tr>
                            <tr>
                                <td><strong>WebM (VP9)</strong></td>
                                <td>.webm</td>
                                <td>Chrome, Firefox, Edge</td>
                                <td>Fallback (smaller file sizes)</td>
                            </tr>
                            <tr>
                                <td><strong>OGG</strong></td>
                                <td>.ogg, .ogv</td>
                                <td>Firefox, Chrome</td>
                                <td>Legacy fallback (rare)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Multiple Format Support</span>
                    </div>
                    <CodeBlock language="html" code={multipleFormatCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Video Compression & Encoding</h2>
                <p>
                    Proper compression reduces file size dramatically without noticeable quality loss.
                </p>

                <div className="optimization-tips">
                    <h3>Compression Best Practices:</h3>
                    <ul>
                        <li><strong>Resolution:</strong> Match target device (1080p for desktop, 720p for mobile)</li>
                        <li><strong>Bitrate:</strong> 5-10 Mbps for 1080p, 2.5-5 Mbps for 720p, 1-2.5 Mbps for 480p</li>
                        <li><strong>Frame Rate:</strong> 30fps is sufficient for most content (24fps for film look)</li>
                        <li><strong>Codec:</strong> H.264 for MP4 (best compatibility), VP9 for WebM (better compression)</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>FFmpeg Compression Command</span>
                    </div>
                    <CodeBlock language="bash" code={ffmpegCompressionCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Video Techniques</h2>

                <h3>1. Adaptive Streaming (HLS/DASH)</h3>
                <p>
                    Serve different quality levels based on user's bandwidth:
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={adaptiveStreamingCode} />
                </div>

                <h3>2. Lazy Loading</h3>
                <p>
                    Don't load videos until they're needed:
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={lazyLoadingCode} />
                </div>

                <h3>3. Poster Images</h3>
                <p>
                    Show a thumbnail before video loads:
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={posterImageCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Video Accessibility</h2>
                <p>
                    Make videos accessible to all users, including those with hearing or vision impairments.
                </p>

                <h3>1. Captions/Subtitles (Required!)</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Adding Captions with WebVTT</span>
                    </div>
                    <CodeBlock language="html" code={captionsCode} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>WebVTT File Format (captions-en.vtt)</span>
                    </div>
                    <CodeBlock language="markdown" code={webVttCode} />
                </div>

                <h3>2. Audio Descriptions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={audioDescriptionCode} />
                </div>

                <h3>3. Transcripts</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={transcriptCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Audio Optimization</h2>
                <p>
                    Audio files require similar optimization strategies.
                </p>

                <h3>Audio Format Comparison:</h3>
                <div className="format-comparison">
                    <table>
                        <thead>
                            <tr>
                                <th>Format</th>
                                <th>Extension</th>
                                <th>Quality</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>MP3</strong></td>
                                <td>.mp3</td>
                                <td>Good (lossy)</td>
                                <td>Universal compatibility</td>
                            </tr>
                            <tr>
                                <td><strong>AAC</strong></td>
                                <td>.aac, .m4a</td>
                                <td>Better (lossy)</td>
                                <td>Higher quality at same bitrate</td>
                            </tr>
                            <tr>
                                <td><strong>OGG Vorbis</strong></td>
                                <td>.ogg</td>
                                <td>Excellent (lossy)</td>
                                <td>Open source alternative</td>
                            </tr>
                            <tr>
                                <td><strong>WAV</strong></td>
                                <td>.wav</td>
                                <td>Perfect (lossless)</td>
                                <td>Avoid (huge files)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Optimized Audio Implementation</span>
                    </div>
                    <CodeBlock language="html" code={optimizedAudioCode} />
                </div>

                <h3>Audio Compression Tips:</h3>
                <ul>
                    <li><strong>Bitrate:</strong> 128kbps for speech, 192kbps for music, 320kbps for high quality</li>
                    <li><strong>Sample Rate:</strong> 44.1kHz is standard (CD quality)</li>
                    <li><strong>Mono vs Stereo:</strong> Use mono for podcasts/speech (half the file size)</li>
                </ul>

                <div className="code-example">
                    <div className="code-header">
                        <span>FFmpeg Audio Compression</span>
                    </div>
                    <CodeBlock language="bash" code={ffmpegAudioCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Video Performance Attributes</h2>

                <div className="attributes-showcase">
                    <div className="attribute-card">
                        <h3><code>preload</code></h3>
                        <p>Controls how much video to preload:</p>
                        <ul>
                            <li><code>none</code> - Don't preload (best for performance)</li>
                            <li><code>metadata</code> - Load only duration, dimensions (recommended)</li>
                            <li><code>auto</code> - Load entire video (only if user likely to play)</li>
                        </ul>
                        <div className="code-example">
                            <CodeBlock language="html" code={preloadCode} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>poster</code></h3>
                        <p>Thumbnail image shown before video plays</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={posterAttributeCode} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>muted</code></h3>
                        <p>Required for autoplay to work (browser policy)</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={mutedAttributeCode} />
                        </div>
                        <Warning><strong> Avoid autoplay:</strong> Users find it annoying and it wastes bandwidth.
                            Only use for muted background videos.</Warning>
                    </div>

                    <div className="attribute-card">
                        <h3><code>playsinline</code></h3>
                        <p>Prevents fullscreen on iOS</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={playsInlineCode} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>CDN & Hosting Best Practices</h2>
                <p>
                    Where you host media files significantly impacts performance.
                </p>

                <div className="hosting-comparison">
                    <div className="option-card">
                        <h3> Bad: Self-Hosted on Web Server</h3>
                        <ul>
                            <li>Slow loading times</li>
                            <li>Expensive bandwidth costs</li>
                            <li>No edge caching</li>
                        </ul>
                    </div>

                    <div className="option-card success">
                        <h3>Good: CDN (Content Delivery Network)</h3>
                        <ul>
                            <li>Fast global delivery</li>
                            <li>Automatic compression</li>
                            <li>Edge caching</li>
                            <li>Examples: Cloudflare, AWS CloudFront, Bunny CDN</li>
                        </ul>
                    </div>

                    <div className="option-card success">
                        <h3>Better: Video Hosting Platform</h3>
                        <ul>
                            <li>Adaptive streaming built-in</li>
                            <li>Automatic transcoding</li>
                            <li>Analytics included</li>
                            <li>Examples: Vimeo, Wistia, Mux</li>
                        </ul>
                    </div>

                    <div className="option-card success">
                        <h3>Best: Specialized Video Platform</h3>
                        <ul>
                            <li>YouTube/Vimeo embeds (free hosting)</li>
                            <li>Automatic quality adjustment</li>
                            <li>Captions, playlists, recommendations</li>
                        </ul>
                        <div className="code-example">
                            <CodeBlock language="html" code={youtubeEmbedCode} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Optimized Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Production-Ready Video Implementation</span>
                    </div>
                    <CodeBlock language="html" code={productionVideoCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Checklist</h2>
                <div className="checklist">
                    <h3>Before Publishing:</h3>
                    <ul>
                        <li> Compressed video to appropriate bitrate</li>
                        <li> Provided multiple formats (WebM + MP4)</li>
                        <li> Added captions/subtitles in WebVTT format</li>
                        <li> Included poster image (optimized JPEG/WebP)</li>
                        <li> Set <code>preload="metadata"</code> or <code>"none"</code></li>
                        <li> Used CDN or video platform for hosting</li>
                        <li> Tested on mobile devices</li>
                        <li> Provided transcript for SEO</li>
                        <li> Avoided autoplay (or used muted)</li>
                        <li> Checked file size is reasonable</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Compress videos for web delivery</li>
                            <li>Provide multiple formats</li>
                            <li>Add captions and transcripts</li>
                            <li>Use <code>preload="metadata"</code></li>
                            <li>Include poster images</li>
                            <li>Host on CDN or video platform</li>
                            <li>Test on real mobile devices</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Upload uncompressed video files</li>
                            <li>Use autoplay with sound</li>
                            <li>Skip captions (illegal in many cases)</li>
                            <li>Host large files on web server</li>
                            <li>Forget mobile optimization</li>
                            <li>Use only one video format</li>
                            <li>Ignore accessibility requirements</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLMedia;


